import type {
  GroupedPropertyFiltersDto,
  SupplierFilterDto,
} from '../../../insights/suppliers/models/supplier-properties.dto.models';
import type {
  CategoryFilter,
  GroupedFilterProperties,
  SupplierFilter,
} from '../../../insights/suppliers/models/supplier.models';
import type {
  CategoryStructure,
  CategoryTree,
} from '../../../insights/suppliers/modules/categorization/models/category-tree.models';
import { Modules } from '../../consts/custom-fields.consts';
import { isArray } from '../is-array/is-array.helper';
import { isNumber } from '../is-number/is-number.helper';
import { isRecord } from '../is-record/is-record.helper';
import { isString } from '../is-string/is-string.helper';

/**
 * Properties grouped by content-area ID (string keys).
 * Each value is either a SupplierFilter-like array (property filters) or a CategoryTree array (category filters).
 */
type PropertiesRecord =
  | GroupedFilterProperties
  | GroupedPropertyFiltersDto
  | Record<string, SupplierFilterDto[] | CategoryTree[]>;

/**
 * Minimal shape sufficient to treat a value as a SupplierFilter during lookup.
 */
interface SupplierFilterCandidate {
  id: number;
  label?: string;
  name?: string;
  module?: string;
  uniqueId?: string;
  relation?: string | Record<string, unknown>;
}

/**
 * Shallow guard: value is a record with numeric id.
 */
function isSupplierFilterCandidate(
  value: unknown,
): value is SupplierFilterCandidate {
  if (!isRecord(value)) return false;
  const id = value['id'];
  return isNumber(id);
}

/**
 * Array guard: confirms the array contains SupplierFilter-like objects.
 * Distinguishes from CategoryTree arrays by requiring a 'type' field on the first element.
 */
function isSupplierFilterArray(
  list: unknown[],
): list is SupplierFilterCandidate[] {
  if (list.length === 0) return false;
  const first = list[0];
  return isSupplierFilterCandidate(first) && 'type' in first;
}

/**
 * Find a Supplier property filter by id across all content areas and add missing metadata.
 *
 * - Preserves existing uniqueId; otherwise falls back to `${module}_${nameOrId}` with `module` defaulting to `Modules.Supplier`.
 * - Preserves existing relation; otherwise uses relation from `slimFilter` (needed for query params).
 *
 * @param slimFilter Minimal filter (must include id; typically also has block_field_id).
 * @param properties Grouped properties and categories keyed by content-area ID as strings.
 * @returns Enriched filter if found; otherwise undefined.
 *
 * @example
 * const slim = { id: 14, block_field_id: 904, uniqueId: 'supplier_global_rating' };
 * const enriched = findAndEnrichPropertyFilter(slim, properties);
 */
function findAndEnrichPropertyFilter(
  slimFilter: Partial<SupplierFilter>,
  properties: PropertiesRecord,
): Partial<SupplierFilter> | undefined {
  for (const blockId of Object.keys(properties)) {
    const list = (properties as Record<string, unknown[]>)[blockId] ?? [];

    if (!isArray(list) || !isSupplierFilterArray(list)) {
      continue;
    }

    const found = list.find((p) => p.id === slimFilter.id);

    if (found !== undefined) {
      const moduleValue = found.module ?? slimFilter.module ?? Modules.Supplier;
      const nameValue = isString(found.name) ? found.name : undefined;
      const uniqueId =
        (isString(found.uniqueId) ? found.uniqueId : undefined) ??
        slimFilter.uniqueId ??
        `${moduleValue}_${nameValue ?? found.id}`;

      const relation = found.relation ?? slimFilter.relation;

      return {
        ...found,
        uniqueId,
        relation,
      };
    }
  }

  return undefined;
}

/**
 * Transform a CategoryTree node (and its children) into a CategoryFilter tree with filtering metadata.
 *
 * - Sets module to Categories and uniqueId to `categories_${slug(name)}`.
 * - Adds the relation object required for building query params.
 *
 * @param categoryTree Category tree from the API.
 * @returns CategoryFilter with module, uniqueId, relation, and enriched children.
 */
function enrichCategoryFilter(categoryTree: CategoryTree): CategoryFilter {
  const enrichCategoryNode = (
    node: CategoryTree | CategoryStructure,
  ): CategoryFilter => {
    const nodeName = (node.name ?? node.label)
      .toLowerCase()
      .replaceAll(/\s+/g, '_');

    const enriched: CategoryFilter = {
      id: node.id,
      label: node.label,
      name: nodeName,
      module: Modules.Categories,
      uniqueId: `categories_${nodeName}`,
      relation: {
        'category_supplier_data::category_supplier_data.category_id': '',
        value: node.id,
      },
      children: [],
    };

    if (node.children !== undefined && node.children.length > 0) {
      enriched.children = node.children.map((child) =>
        enrichCategoryNode(child),
      );
    }

    return enriched;
  };

  return enrichCategoryNode(categoryTree);
}

/**
 * Enrich a minimal filter by resolving metadata from property groups or category trees.
 *
 * - If the filter already has `type`, it is treated as already enriched and returned as-is.
 * - Category filters: require `module === Modules.Categories` and `categoriesAreaId` pointing to the categories list in `properties`.
 * - Property filters: require `block_field_id` and are matched by `id` across content areas.
 *
 * @param slimFilter Minimal filter from localStorage.
 * @param properties Grouped lists keyed by content-area ID (strings).
 * @param categoriesAreaId Content-area ID (stringified key) where CategoryTree[] is stored.
 * @returns A fully enriched filter, or the original slim filter if not resolvable.
 */
export function enrichSupplierFilter(
  slimFilter: Partial<SupplierFilter>,
  properties: PropertiesRecord,
  categoriesAreaId?: number,
): SupplierFilter | Partial<SupplierFilter> {
  if (slimFilter.type !== undefined) {
    return slimFilter;
  }

  const isCategoryFilter = slimFilter.module === Modules.Categories;

  if (isCategoryFilter) {
    if (categoriesAreaId === undefined) {
      return slimFilter;
    }

    const maybeCategoryList = properties[String(categoriesAreaId)] ?? [];
    const isCategoryTree = (value: unknown): value is CategoryTree =>
      isRecord(value) && isNumber(value['id']) && isArray(value['children']);
    const categoryTrees: CategoryTree[] =
      isArray(maybeCategoryList) ?
        (maybeCategoryList as unknown[]).filter((value) =>
          isCategoryTree(value),
        )
      : [];
    const enrichedFilter = categoryTrees.find(
      (tree) => tree.id === slimFilter.id,
    );

    if (enrichedFilter !== undefined) {
      return enrichCategoryFilter(enrichedFilter);
    }
  }

  if (slimFilter.block_field_id !== undefined) {
    const found = findAndEnrichPropertyFilter(slimFilter, properties);
    if (found !== undefined) {
      return found;
    }
  }

  return slimFilter;
}
